import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api, apiPut } from '../lib/api'
import '../styles/widget-gallery.css'

interface Widget {
    id: string
    label: string
    description: string
    category: string
    dashboardTypes: string
    defaultW: number
    defaultH: number
    isEnabled: boolean
}

const ALL_DASHBOARD_TYPES = ['daily', 'weekly', 'monthly', 'seasonal', 'annual']

export default function AdminWidgetsPage() {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [editing, setEditing] = useState<string | null>(null)
    const [editDashTypes, setEditDashTypes] = useState<string[]>([])

    const { data, isLoading } = useQuery<{ widgets: Widget[] }>({
        queryKey: ['admin-widgets'],
        queryFn: () => api('/api/admin/dashboard/widgets', token),
    })

    const updateMutation = useMutation({
        mutationFn: (params: { id: string; body: any }) =>
            apiPut(`/api/admin/dashboard/widgets/${params.id}`, token, params.body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-widgets'] }),
    })

    const startEditTypes = (w: Widget) => {
        setEditing(w.id)
        setEditDashTypes(w.dashboardTypes.split(',').map(s => s.trim()).filter(Boolean))
    }

    const saveTypes = (id: string) => {
        updateMutation.mutate({ id, body: { dashboardTypes: editDashTypes.join(',') } })
        setEditing(null)
    }

    const toggleType = (dt: string) => {
        setEditDashTypes(prev =>
            prev.includes(dt) ? prev.filter(t => t !== dt) : [...prev, dt]
        )
    }

    const toggleEnabled = (w: Widget) => {
        updateMutation.mutate({ id: w.id, body: { isEnabled: !w.isEnabled } })
    }

    const widgets = data?.widgets ?? []

    return (
        <>
            <div className="page-header">
                <h1>🧩 Widget Management</h1>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    Configure which dashboard widgets are available and which dashboard types they belong to.
                </p>
            </div>

            {isLoading ? (
                <div className="dashboard-loading"><div className="spinner" /><span>Loading widgets…</span></div>
            ) : (
                <div className="chart-panel" style={{ marginTop: 'var(--space-4)' }}>
                    <table className="top-sellers-table" style={{ tableLayout: 'auto' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>On</th>
                                <th>Widget</th>
                                <th>Category</th>
                                <th>Dashboard Types</th>
                                <th style={{ width: 80 }}>Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            {widgets.map(w => (
                                <tr key={w.id} style={{ opacity: w.isEnabled ? 1 : 0.5 }}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={w.isEnabled}
                                            onChange={() => toggleEnabled(w)}
                                            style={{ accentColor: 'var(--color-primary)' }}
                                        />
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{w.label}</div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{w.description}</div>
                                    </td>
                                    <td>
                                        <span className="gallery-card-category">{w.category}</span>
                                    </td>
                                    <td>
                                        {editing === w.id ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                                {ALL_DASHBOARD_TYPES.map(dt => (
                                                    <label key={dt} style={{
                                                        display: 'flex', alignItems: 'center', gap: 4,
                                                        fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)',
                                                        cursor: 'pointer',
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={editDashTypes.includes(dt)}
                                                            onChange={() => toggleType(dt)}
                                                            style={{ accentColor: 'var(--color-primary)' }}
                                                        />
                                                        {dt}
                                                    </label>
                                                ))}
                                                <button
                                                    onClick={() => saveTypes(w.id)}
                                                    style={{
                                                        padding: '2px 8px', fontSize: 'var(--text-xs)',
                                                        background: 'var(--color-primary)', color: 'white',
                                                        border: 'none', borderRadius: 'var(--radius-sm)',
                                                        cursor: 'pointer', fontWeight: 600, marginLeft: 4,
                                                    }}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditing(null)}
                                                    style={{
                                                        padding: '2px 8px', fontSize: 'var(--text-xs)',
                                                        background: 'transparent', color: 'var(--color-text-tertiary)',
                                                        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                style={{ display: 'flex', flexWrap: 'wrap', gap: 4, cursor: 'pointer' }}
                                                onClick={() => startEditTypes(w)}
                                                title="Click to edit"
                                            >
                                                {w.dashboardTypes.split(',').map(dt => (
                                                    <span key={dt} style={{
                                                        padding: '1px 6px', borderRadius: 'var(--radius-sm)',
                                                        background: 'var(--color-primary-surface)',
                                                        color: 'var(--color-primary-light)',
                                                        fontSize: 'var(--text-xs)', fontWeight: 500,
                                                    }}>
                                                        {dt.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                                        {w.defaultW}×{w.defaultH}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    )
}
